import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

/**
 * Candidate-shell ambient background — visual only, never a data readout. Two states:
 *
 * - idle: slow CSS gradient-blob drift (no JS), running behind every pre-interview step.
 * - "ai" / "candidate": InterviewStep hands us a turn-taking cue + a 0–1 `level` and we draw
 *   a reactive waveform bar. For "candidate" the level is REAL mic amplitude (InterviewStep
 *   measures its own dedicated getUserMedia stream via useMicLevel — never presented as a
 *   transcript-accuracy or confidence figure, purely a visual cue). For "ai" there is no
 *   accessible amplitude — browsers don't expose speechSynthesis output to Web Audio — so
 *   InterviewStep instead hands us a smooth synthesized pulse. Nothing on screen claims the
 *   "ai" wave reflects Asha's real voice; it's a deliberate design choice for the turn-taking
 *   cue described in the redesign brief, distinguished from "candidate" only by color/shape.
 */
export type AudioMode = "idle" | "ai" | "candidate";
export interface AudioCue {
  mode: AudioMode;
  /** 0–1. Real mic RMS for "candidate"; a synthesized pulse for "ai"; ignored for "idle". */
  level: number;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

export function CandidateBackground({ mode, level }: AudioCue): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const historyRef = useRef<number[]>(new Array(48).fill(0));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || mode === "idle") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = prefersReducedMotion();
    const color = mode === "ai" ? "43, 106, 55" : "140, 194, 42"; // ai: brand forest-green · candidate: lime (Theme 1)

    const draw = () => {
      const { width, height } = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const hist = historyRef.current;
      hist.push(Math.max(0, Math.min(1, level)));
      hist.shift();

      const barW = width / hist.length;
      ctx.fillStyle = `rgba(${color}, 0.35)`;
      hist.forEach((v, i) => {
        const h = Math.max(3, v * height);
        ctx.fillRect(i * barW, (height - h) / 2, barW * 0.6, h);
      });

      if (!reduced) rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [mode, level]);

  return (
    <div className="cx-bg" aria-hidden="true">
      <div className="cx-bg-blob a" />
      <div className="cx-bg-blob b" />
      <div className="cx-bg-blob c" />
      {mode !== "idle" && <canvas ref={canvasRef} className={`cx-bg-wave ${mode}`} />}
    </div>
  );
}
